import React, { useState } from 'react';
import { Users, Plus, Edit, Trash2, Search, User, Phone, Mail, MapPin, Building, Calendar, Briefcase, UserCheck, Clock, DollarSign, Grid, List } from 'lucide-react';
import { formatDZD } from '../utils/currency';
import { useEmployees } from '../contexts/EmployeesContext';
import { useSettings } from '../contexts/SettingsContext';
import './Employees.css';

const Employees = () => {
  const { 
    employees, 
    addEmployee, 
    updateEmployee, 
    deleteEmployee, 
    getDepartments,
    getPositions,
    getManagerOptions,
    getTotalSalaries
  } = useEmployees();
  const { settings } = useSettings();

  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWorkType, setFilterWorkType] = useState('all');
  const [viewMode, setViewMode] = useState('cards');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    position: '',
    department: '',
    salary: '',
    hireDate: '',
    status: 'active',
    employeeId: '',
    manager: '',
    workType: 'full-time',
    skills: []
  });

  // Get departments from context
  const departments = getDepartments();
  
  // Get positions from context
  const positions = getPositions();

  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar',
    'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger',
    'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma',
    'Constantine', 'Médéa', 'Mostaganem', 'MSila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh'
  ];

  const workTypes = {
    'full-time': { label: 'Temps plein', icon: Clock },
    'part-time': { label: 'Temps partiel', icon: Clock },
    'contract': { label: 'Contractuel', icon: Briefcase },
    'intern': { label: 'Stagiaire', icon: User }
  };

  const filteredEmployees = employees.filter(employee => {
    const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || employee.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || employee.status === filterStatus;
    const matchesWorkType = filterWorkType === 'all' || employee.workType === filterWorkType;
    
    return matchesSearch && matchesDepartment && matchesStatus && matchesWorkType;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingEmployee) {
      updateEmployee(editingEmployee.id, formData);
    } else {
      addEmployee(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      position: '',
      department: '',
      salary: '',
      hireDate: '',
      status: 'active',
      employeeId: '',
      manager: '',
      workType: 'full-time',
      skills: []
    });
    setEditingEmployee(null);
    setShowModal(false);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phone: employee.phone,
      address: employee.address,
      city: employee.city,
      postalCode: employee.postalCode,
      position: employee.position,
      department: employee.department,
      salary: employee.salary,
      hireDate: employee.hireDate,
      status: employee.status,
      employeeId: employee.employeeId,
      manager: employee.manager,
      workType: employee.workType,
      skills: employee.skills || []
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) {
      deleteEmployee(id);
    }
  };

  const stats = {
    total: employees.length,
    active: employees.filter(e => e.status === 'active').length,
    fullTime: employees.filter(e => e.workType === 'full-time').length,
    departments: new Set(employees.map(e => e.department)).size,
    totalSalaries: getTotalSalaries()
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="employees fade-in">
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <Users size={32} />
          Gestion des Employés
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>Gérez votre équipe et les informations du personnel</p>
      </div>

      {/* Toolbar */}
      <div className="employees-toolbar">
        <div className="search-filters">
          <div className="search-input">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            value={filterDepartment} 
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous départements</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
          <select 
            value={filterWorkType} 
            onChange={(e) => setFilterWorkType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous types</option>
            {Object.entries(workTypes).map(([key, type]) => (
              <option key={key} value={key}>{type.label}</option>
            ))}
          </select>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
        
        <div className="toolbar-right">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              <Grid size={18} />
            </button>
            <button
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              <List size={18} />
            </button>
          </div>

          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <Plus size={20} />
            Nouvel employé
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="employees-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total employés</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon active">
            <UserCheck size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.active}</span>
            <span className="stat-label">Actifs</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon fulltime">
            <Clock size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.fullTime}</span>
            <span className="stat-label">Temps plein</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon departments">
            <Building size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.departments}</span>
            <span className="stat-label">Départements</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon salary">
            <DollarSign size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{formatDZD(stats.totalSalaries)}</span>
            <span className="stat-label">Masse salariale</span>
          </div>
        </div>
      </div>

      {/* Employees Display */}
      {viewMode === 'cards' ? (
        <div className="employees-grid">
          {filteredEmployees.map(employee => {
            const WorkTypeIcon = workTypes[employee.workType].icon;
            return (
              <div key={employee.id} className="employee-card">
                <div className="employee-header">
                  <div className="employee-avatar">
                    <span>{employee.firstName.charAt(0)}{employee.lastName.charAt(0)}</span>
                  </div>
                  <div className="employee-basic">
                    <h3 className="employee-name">{employee.firstName} {employee.lastName}</h3>
                    <div className="employee-id">ID: {employee.employeeId}</div>
                  </div>
                  <div className="employee-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleEdit(employee)}
                      title="Modifier"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-icon delete"
                      onClick={() => handleDelete(employee.id)}
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="employee-content">
                  <div className="employee-position">
                    <Briefcase size={14} />
                    <span><strong>{employee.position}</strong></span>
                  </div>
                  <div className="employee-department">
                    <Building size={14} />
                    <span>{employee.department}</span>
                  </div>
                  <div className="employee-work-type">
                    <WorkTypeIcon size={14} />
                    <span>{workTypes[employee.workType].label}</span>
                  </div>
                  <div className="employee-email">
                    <Mail size={14} />
                    <span>{employee.email}</span>
                  </div>
                  <div className="employee-phone">
                    <Phone size={14} />
                    <span>{employee.phone}</span>
                  </div>
                  <div className="employee-location">
                    <MapPin size={14} />
                    <span>{employee.city}</span>
                  </div>
                  
                  {employee.skills && employee.skills.length > 0 && (
                    <div className="employee-skills">
                      <strong>Compétences:</strong>
                      <div className="skills-tags">
                        {employee.skills.slice(0, 3).map((skill, index) => (
                          <span key={index} className="skill-tag">{skill}</span>
                        ))}
                        {employee.skills.length > 3 && (
                          <span className="skill-tag more">+{employee.skills.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="employee-stats">
                  <div className="stat">
                    <span className="stat-value">{formatDZD(employee.salary)}</span>
                    <span className="stat-label">Salaire</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{formatDate(employee.hireDate)}</span>
                    <span className="stat-label">Embauche</span>
                  </div>
                </div>

                <div className="employee-footer">
                  <span className="employee-manager">Manager: {employee.manager}</span>
                  <span className={`employee-status ${employee.status}`}>
                    {employee.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="employees-table-container">
          <div className="table-header">
            <div>Employé</div>
            <div>ID</div>
            <div>Poste</div>
            <div>Département</div>
            <div>Email</div>
            <div>Téléphone</div>
            <div>Salaire</div>
            <div>Statut</div>
            <div>Actions</div>
          </div>
          
          {filteredEmployees.map(employee => {
            const WorkTypeIcon = workTypes[employee.workType].icon;
            return (
              <div key={employee.id} className="table-row">
                <div className="table-cell">
                  <div className="employee-info">
                    <div className="employee-avatar-small">
                      <span>{employee.firstName.charAt(0)}{employee.lastName.charAt(0)}</span>
                    </div>
                    <div className="employee-details">
                      <span className="employee-name-table">{employee.firstName} {employee.lastName}</span>
                      <span className="employee-work-type-table">
                        <WorkTypeIcon size={12} />
                        {workTypes[employee.workType].label}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="table-cell employee-id-cell">{employee.employeeId}</div>
                <div className="table-cell">{employee.position}</div>
                <div className="table-cell">{employee.department}</div>
                <div className="table-cell">
                  <div className="contact-info">
                    <Mail size={14} />
                    <span>{employee.email}</span>
                  </div>
                </div>
                <div className="table-cell">
                  <div className="contact-info">
                    <Phone size={14} />
                    <span>{employee.phone}</span>
                  </div>
                </div>
                <div className="table-cell">{formatDZD(employee.salary)}</div>
                <div className="table-cell">
                  <span className={`employee-status ${employee.status}`}>
                    {employee.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="table-cell actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleEdit(employee)}
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(employee.id)}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}</h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="employee-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    placeholder="Ahmed"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    placeholder="Belkacem"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="ahmed.belkacem@bridge-it.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+213 21 123 456"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="15 Rue Didouche Mourad"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ville/Wilaya</label>
                  <select
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  >
                    <option value="">Sélectionner une wilaya</option>
                    {wilayas.map(wilaya => (
                      <option key={wilaya} value={wilaya}>{wilaya}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Code postal</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                    placeholder="16000"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Poste *</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner un poste</option>
                    {positions.map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Département *</label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner un département</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de contrat</label>
                  <select
                    value={formData.workType}
                    onChange={(e) => setFormData({...formData, workType: e.target.value})}
                  >
                    {Object.entries(workTypes).map(([key, type]) => (
                      <option key={key} value={key}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Salaire (DZD)</label>
                  <input
                    type="number"
                    value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: parseInt(e.target.value) || 0})}
                    placeholder="80000"
                  />
                </div>
                <div className="form-group">
                  <label>Date d'embauche</label>
                  <input
                    type="date"
                    value={formData.hireDate}
                    onChange={(e) => setFormData({...formData, hireDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ID Employé</label>
                  <input
                    type="text"
                    value={formData.employeeId}
                    onChange={(e) => setFormData({...formData, employeeId: e.target.value})}
                    placeholder="EMP001"
                  />
                </div>
                <div className="form-group">
                  <label>Manager</label>
                  <select
                    value={formData.manager}
                    onChange={(e) => setFormData({...formData, manager: e.target.value})}
                  >
                    <option value="">Sélectionner un manager</option>
                    {getManagerOptions().map(manager => (
                      <option key={manager} value={manager}>{manager}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Compétences</label>
                <input
                  type="text"
                  value={formData.skills.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData, 
                    skills: e.target.value.split(', ').filter(s => s.trim())
                  })}
                  placeholder="React, Node.js, JavaScript, Python"
                />
                <small>Séparez les compétences par des virgules</small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingEmployee ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees; 